/**
 * Tenant Admin → Users page.
 *
 * Lists the tenant's users (sourced from Cognito), invites new admins,
 * promotes/demotes roles, resets a customer's password (Cognito emails
 * them a reset), and enables/disables accounts. All actions are
 * tenant-scoped and role-gated server-side; self-destructive actions are
 * hidden here as a courtesy (the API also rejects them).
 */

import { useCallback, useEffect, useState } from "react";

import {
  inviteAdmin,
  listAdminUsers,
  resetUserPassword,
  setUserRole,
  setUserStatus,
} from "@starz/shared/api/sdk.gen";
import type { AdminUserSummary, ApiError } from "@starz/shared/api/types.gen";
import { useAuthUser } from "@/lib/auth/useAuth";

function roleLabel(role: string): string {
  return role === "tenant_admin" ? "Admin" : role === "platform_admin" ? "Platform" : "Customer";
}

function statusLabel(u: AdminUserSummary): string {
  if (!u.enabled) return "Disabled";
  if (u.status === "FORCE_CHANGE_PASSWORD") return "Invite pending";
  if (u.status === "CONFIRMED") return "Active";
  return u.status || "—";
}

function fullName(u: AdminUserSummary): string {
  return `${u.first_name} ${u.last_name}`.trim() || "—";
}

export function AdminUsers() {
  const me = useAuthUser();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // username being acted on

  // Invite form
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [inviting, setInviting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listAdminUsers();
    if ("error" in result) setError(result.error.error.message);
    else {
      setUsers(result.data);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    setError(null);
    setNotice(null);
    const result = await inviteAdmin({ email: email.trim(), firstName, lastName });
    setInviting(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotice(`Invited ${email.trim()} — they'll get an email to set their password.`);
    setEmail("");
    setFirstName("");
    setLastName("");
    void refresh();
  }

  async function runAction(
    username: string,
    fn: () => Promise<{ data: unknown } | { error: ApiError }>,
    ok: string,
  ) {
    setBusy(username);
    setError(null);
    setNotice(null);
    const result = await fn();
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotice(ok);
    void refresh();
  }

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Team
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy">
        Users &amp; <em className="text-starz-gold">admins</em>.
      </h1>
      <div className="mb-6 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      {error && (
        <p className="mb-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {notice && (
        <p className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </p>
      )}

      {/* Invite */}
      <form
        onSubmit={onInvite}
        className="mb-8 flex flex-wrap items-end gap-3 rounded-xl border border-starz-gold/20 bg-white p-4"
      >
        <div className="flex flex-col">
          <label className="text-xs font-medium text-warm-gray">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new.admin@company.com"
            className="min-w-[16rem] rounded-md border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-warm-gray">First name</label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="rounded-md border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-warm-gray">Last name</label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="rounded-md border border-starz-gold/30 px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={inviting}
          className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90 disabled:opacity-60"
        >
          {inviting ? "Inviting…" : "Invite admin"}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-warm-gray">Loading users…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-starz-gold/15">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-starz-cream/50 text-xs uppercase tracking-wide text-warm-gray">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = me?.user_id === u.user_id;
                const rowBusy = busy === u.username;
                return (
                  <tr key={u.username} className="border-t border-starz-gold/10">
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{fullName(u)}</td>
                    <td className="px-4 py-3">{roleLabel(u.role)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          !u.enabled
                            ? "text-rose-700"
                            : u.status === "FORCE_CHANGE_PASSWORD"
                              ? "text-amber-700"
                              : "text-emerald-700"
                        }
                      >
                        {statusLabel(u)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {u.role === "customer" && (
                          <button
                            type="button"
                            disabled={rowBusy}
                            onClick={() =>
                              void runAction(
                                u.username,
                                () => setUserRole({ username: u.username, role: "tenant_admin" }),
                                `${u.email} is now an admin.`,
                              )
                            }
                            className="rounded border border-starz-gold/40 px-2 py-1 text-xs hover:bg-starz-cream disabled:opacity-50"
                          >
                            Make admin
                          </button>
                        )}
                        {u.role === "tenant_admin" && !isSelf && (
                          <button
                            type="button"
                            disabled={rowBusy}
                            onClick={() =>
                              void runAction(
                                u.username,
                                () => setUserRole({ username: u.username, role: "customer" }),
                                `${u.email} is no longer an admin.`,
                              )
                            }
                            className="rounded border border-starz-gold/40 px-2 py-1 text-xs hover:bg-starz-cream disabled:opacity-50"
                          >
                            Remove admin
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={rowBusy}
                          onClick={() =>
                            void runAction(
                              u.username,
                              () => resetUserPassword({ username: u.username }),
                              `Sent a password reset to ${u.email}.`,
                            )
                          }
                          className="rounded border border-starz-gold/40 px-2 py-1 text-xs hover:bg-starz-cream disabled:opacity-50"
                        >
                          Reset password
                        </button>
                        {!isSelf &&
                          (u.enabled ? (
                            <button
                              type="button"
                              disabled={rowBusy}
                              onClick={() =>
                                void runAction(
                                  u.username,
                                  () => setUserStatus({ username: u.username, enabled: false }),
                                  `Disabled ${u.email}.`,
                                )
                              }
                              className="rounded border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            >
                              Disable
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={rowBusy}
                              onClick={() =>
                                void runAction(
                                  u.username,
                                  () => setUserStatus({ username: u.username, enabled: true }),
                                  `Enabled ${u.email}.`,
                                )
                              }
                              className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              Enable
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-warm-gray">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
